import { useSessionStorage } from '@vueuse/core';
import { Checkout } from '@vp/common';

type CustomerInfo = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
};

type Agreement = {
  key: string;
  value: boolean;
};

export type CustomerInformation = {
  address?: Partial<Checkout.Address.Address>;
  customer?: Partial<CustomerInfo>;
  agreements?: Agreement[];
  companyName?: string;
  vatId?: string;
  paymentIntentId?: string;
};

export type OrderDetails = {
  eventId?: number;
  customerInformation: CustomerInformation;
};

export const useOrderDetails = (initial?: OrderDetails) => {
  const initialCustomerInformation = {
    customer: {},
    address: {},
    agreements: [],
    ...(initial?.customerInformation || {}),
  };

  const orderDetails = useSessionStorage<OrderDetails>('order-details', {
    eventId: initial?.eventId,
    customerInformation: initialCustomerInformation,
  });

  const setAddress = (
    address: Pick<Checkout.Address.Address, 'country' | 'city'> & Partial<Checkout.Address.Address> & { line1?: string; line2?: string | null; postal_code?: string },
  ): void => {
    if (!orderDetails.value.customerInformation) {
      orderDetails.value.customerInformation = {};
    }

    orderDetails.value.customerInformation.address = {
      country: address.country,
      state: address.state,
      postalCode: address.postal_code ?? address.postalCode,
      city: address.city,
      street1: address.street1 ?? address.line1,
      street2: address.street1 ?? address.line2 ?? undefined,
    };
  };

  const setCustomer = (customer: Partial<CustomerInfo> & { name?: string }): void => {
    if (!orderDetails.value.customerInformation) {
      orderDetails.value.customerInformation = {};
    }

    if (!orderDetails.value.customerInformation.customer) {
      orderDetails.value.customerInformation.customer = {};
    }

    orderDetails.value.customerInformation.customer.email = customer.email;
    orderDetails.value.customerInformation.customer.phoneNumber = customer.phoneNumber;
    if (customer.firstName || customer.lastName) {
      orderDetails.value.customerInformation.customer.firstName = customer.firstName;
      orderDetails.value.customerInformation.customer.lastName = customer.lastName;
    } else if (customer.name) {
      const [first, last] = customer.name
        .trim()
        .split(/\s(.*)/s)
        .map(s => s.trim());
      orderDetails.value.customerInformation.customer.firstName = first || '';
      orderDetails.value.customerInformation.customer.lastName = last || '';
    }
  };

  const setAgreements = (agreements: Agreement[]): void => {
    if (!orderDetails.value.customerInformation) {
      orderDetails.value.customerInformation = {};
    }

    orderDetails.value.customerInformation.agreements = [...agreements];
  };

  const setPaymentIntentId = (paymentIntentId?: string): void => {
    if (!orderDetails.value.customerInformation) {
      orderDetails.value.customerInformation = {};
    }

    orderDetails.value.customerInformation.paymentIntentId = paymentIntentId;
  };

  const clear = () => {
    orderDetails.value = null;
  };

  return {
    orderDetails,
    setAddress,
    setCustomer,
    setAgreements,
    setPaymentIntentId,
    clear,
  };
};
